import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditHrEmployeeSelectField } from '../../../EditHrEmployeeSelectField';
import type { ContactIndiaResearchData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPSERT_INDIA_RESEARCH = gql`
  mutation UpsertContactIndiaResearch(
    $contactId: ID!
    $values: ContactIndiaResearchInput!
  ) {
    upsertContactIndiaResearch(contactId: $contactId, values: $values) {
      ok
      created
      contactIndiaResearch {
        id
        indiaContactOwnerFollowupDate
        indiaResearchDate
        india1QaCheckNeeded
        india1QaCheckedBy {
          id
          name
        }
        lastIndia1QaCheckedBy {
          id
          name
        }
        lastIndia1QaDate
        india2QaCheckNeeded
        india2QaCheckedBy {
          id
          name
        }
        lastIndia2QaCheckedBy {
          id
          name
        }
        lastIndia2QaDate
      }
    }
  }
`;

const INDIA_RESEARCH_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean; isDate?: boolean }
> = {
  india1QaCheckedById: { apiField: 'india1QaCheckedBy' },
  india1QaCheckNeeded: { apiField: 'india1QaCheckNeeded', isBoolean: true },
  india2QaCheckedById: { apiField: 'india2QaCheckedBy' },
  india2QaCheckNeeded: { apiField: 'india2QaCheckNeeded', isBoolean: true },
  indiaContactOwnerFollowupDate: {
    apiField: 'indiaContactOwnerFollowupDate',
    isDate: true
  },
  indiaResearchDate: { apiField: 'indiaResearchDate', isDate: true },
  lastIndia1QaCheckedById: { apiField: 'lastIndia1QaCheckedBy' },
  lastIndia1QaDate: { apiField: 'lastIndia1QaDate', isDate: true },
  lastIndia2QaCheckedById: { apiField: 'lastIndia2QaCheckedBy' },
  lastIndia2QaDate: { apiField: 'lastIndia2QaDate', isDate: true }
};

export interface EditIndiaResearchDialogProps {
  contactId: string;
  initialValue: ContactIndiaResearchData;
  isOpen: boolean;
  onChange: (data: ContactIndiaResearchData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactIndiaResearchData;
}

export function EditIndiaResearchDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditIndiaResearchDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertIndiaResearch] = useMutation(UPSERT_INDIA_RESEARCH, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertIndiaResearch({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactIndiaResearch?: { ok?: boolean };
      };

      if (data.upsertContactIndiaResearch?.ok === false) {
        toast.error('Failed to save India Research');
      } else {
        toast.success('India Research saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving India Research:', error);
      toast.error('Failed to save India Research');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit India Research"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="India Contact Owner Followup Date"
          onChange={v => {
            onChange({ ...value, indiaContactOwnerFollowupDate: v });
          }}
          type="date"
          value={toDateInputValue(value.indiaContactOwnerFollowupDate) ?? ''}
        />
        <EditFormField
          label="India Research Date"
          onChange={v => {
            onChange({ ...value, indiaResearchDate: v });
          }}
          type="date"
          value={toDateInputValue(value.indiaResearchDate) ?? ''}
        />

        <div className="col-span-2 mt-2">
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            India1 QA
          </h4>
          <div className="grid grid-cols-2 gap-6">
            <EditCheckboxField
              checked={value.india1QaCheckNeeded}
              label="India1 - QA Check Needed"
              onCheckedChange={v => {
                onChange({ ...value, india1QaCheckNeeded: v });
              }}
            />
            <EditHrEmployeeSelectField
              label="India1 QA Checked By"
              onChange={(id, name) => {
                onChange({
                  ...value,
                  india1QaCheckedBy: name,
                  india1QaCheckedById: id || null
                });
              }}
              value={
                value.india1QaCheckedById
                  ? {
                      id: value.india1QaCheckedById,
                      name: value.india1QaCheckedBy
                    }
                  : null
              }
            />
            <EditHrEmployeeSelectField
              label="Last India1 QA Checked By"
              onChange={(id, name) => {
                onChange({
                  ...value,
                  lastIndia1QaCheckedBy: name,
                  lastIndia1QaCheckedById: id || null
                });
              }}
              value={
                value.lastIndia1QaCheckedById
                  ? {
                      id: value.lastIndia1QaCheckedById,
                      name: value.lastIndia1QaCheckedBy
                    }
                  : null
              }
            />
            <EditFormField
              label="Last India1 QA Date"
              onChange={v => {
                onChange({ ...value, lastIndia1QaDate: v });
              }}
              type="date"
              value={toDateInputValue(value.lastIndia1QaDate) ?? ''}
            />
          </div>
        </div>

        <div className="col-span-2 mt-2">
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            India2 QA
          </h4>
          <div className="grid grid-cols-2 gap-6">
            <EditCheckboxField
              checked={value.india2QaCheckNeeded}
              label="India2 - QA Check Needed"
              onCheckedChange={v => {
                onChange({ ...value, india2QaCheckNeeded: v });
              }}
            />
            <EditHrEmployeeSelectField
              label="India2 QA Checked By"
              onChange={(id, name) => {
                onChange({
                  ...value,
                  india2QaCheckedBy: name,
                  india2QaCheckedById: id || null
                });
              }}
              value={
                value.india2QaCheckedById
                  ? {
                      id: value.india2QaCheckedById,
                      name: value.india2QaCheckedBy
                    }
                  : null
              }
            />
            <EditHrEmployeeSelectField
              label="Last India2 QA Checked By"
              onChange={(id, name) => {
                onChange({
                  ...value,
                  lastIndia2QaCheckedBy: name,
                  lastIndia2QaCheckedById: id || null
                });
              }}
              value={
                value.lastIndia2QaCheckedById
                  ? {
                      id: value.lastIndia2QaCheckedById,
                      name: value.lastIndia2QaCheckedBy
                    }
                  : null
              }
            />
            <EditFormField
              label="Last India2 QA Date"
              onChange={v => {
                onChange({ ...value, lastIndia2QaDate: v });
              }}
              type="date"
              value={toDateInputValue(value.lastIndia2QaDate) ?? ''}
            />
          </div>
        </div>
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactIndiaResearchData,
  initial: ContactIndiaResearchData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(INDIA_RESEARCH_FIELDS)) {
    const currentVal = current[uiField as keyof ContactIndiaResearchData];
    const initialVal = initial[uiField as keyof ContactIndiaResearchData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else if (config.isDate) {
        changes[config.apiField] = toDateInputValue(currentVal as string);
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}

function toDateInputValue(dateStr: string): null | string {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}
